import { define, dispatch, html, store, router } from "hybrids";

import markdown from "~/utils/markdown";
import Comment from "~/stores/Comment";
import Session from "~/stores/Session";
import Broadcast from "~/stores/Broadcast";
import ArtistVideo from "~/stores/ArtistVideo";

import User from "../views/User";

export interface VBroadcastChatComment extends HTMLElement {
  context: Broadcast | ArtistVideo;
  comment: Comment;
  session: Session;
  quote: boolean;
  href: string;
  variant: string;
  current: boolean;
  manageable: boolean;
}

export interface CommentClickEvent extends Event {
  detail: {
    comment: Comment;
  };
}

function dispatchEvent(eventName: string) {
  return (host: VBroadcastChatComment) => {
    dispatch(host, eventName, { detail: { comment: host.comment } });
  };
}

export default define<VBroadcastChatComment>({
  tag: "v-broadcast-chat-comment",
  context: undefined,
  comment: undefined,
  session: store(Session),
  quote: false,
  href: "",
  variant: ({ comment }) => (comment.user.name.length % 2 ? "blue" : "pink"),
  current: ({ session, comment }) =>
    store.ready(session) && session.user === comment.user,
  manageable: (host) =>
    host.current || host.context.permissions.canManageAllComments,
  render: ({
    comment,
    context,
    session,
    variant,
    quote,
    href,
    current,
    manageable,
  }) => html`
    <div
      id="container"
      class="${{ [variant]: variant, quote }}"
    >
      ${href
        ? html`
            <v-ui-link id="avatar" block href="${href}">
              <v-broadcast-user broadcast="${context}" user="${comment.user}">
              </v-broadcast-user>
            </v-ui-link>
          `
        : html`
            <v-broadcast-user
              id="avatar"
              broadcast="${context}"
              user="${comment.user}"
            ></v-broadcast-user>
          `}
      <div id="content" class="${{ current }}">
        <v-ui-text
          id="name"
          weight="semibold"
          variant="${quote ? "caption" : ""}"
          selectable
          ygutter="1"
        >
          ${href
            ? html`
                <v-ui-link href="${href}">
                  ${comment.firstName} ${comment.lastName}
                </v-ui-link>
              `
            : html`${comment.firstName} ${comment.lastName}`}
        </v-ui-text>
        ${!quote &&
        comment.parent &&
        html`<v-ui-text ygutter="1">
          <v-broadcast-chat-comment
            comment="${comment.parent}"
            context="${context}"
            quote
          ></v-broadcast-chat-comment>
        </v-ui-text>`}
        <v-ui-text variant="${quote ? "caption" : ""}" weight="normal" ygutter="1">
          <slot></slot>
        </v-ui-text>
      </div>
      <v-ui-text
        id="time"
        color="text-secondary"
        variant="caption"
        weight="normal"
      >
        ${comment.time}
      </v-ui-text>
      ${store.ready(session) &&
      session.user &&
      html`<v-ui-text id="actions" variant="caption" weight="normal">
        <v-ui-button
          variant="inline"
          onclick="${dispatchEvent("replay")}"
          name="Reply"
        ></v-ui-button>
        ${manageable &&
        html`<v-ui-button
          variant="inline"
          onclick="${dispatchEvent("remove")}"
          name="Remove"
        ></v-ui-button>`}
      </v-ui-text>
      <v-ui-button
        variant="inline"
        id="likes"
        onclick="${dispatchEvent("togglelike")}"
      >
        <v-ui-text variant="caption" weight="normal">
          ${context.likedComments.includes(comment)
            ? html`<v-ui-icon
                name="heartfill"
                color="primary"
                size="small"
              ></v-ui-icon>`
            : html`<v-ui-icon
                name="heart"
                color="text-secondary"
                size="small"
              ></v-ui-icon>`}
          ${comment.likes}
        </v-ui-text>
      </v-ui-button>`}
    </div>
  `.css`
    @keyframes fade-in {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1 }
    }

    :host {
      display: grid;
      grid: 1fr / auto;
      margin-bottom: 20px;
      --v-broadcast-chat-comment-color: white;
    }

    #container {
      display: grid;
      grid: min-content auto / 36px min-content 1fr max-content;
      grid-gap: 10px;
    }

    #container.quote {
      grid: 1fr / auto;
    }

    #container.quote #avatar,
    #container.quote #time,
    #container.quote #actions,
    #container.quote #likes {
      display: none;
    }

    #container.pink {
      --v-broadcast-chat-comment-color: #F461EF;
    }

    #container.blue {
      --v-broadcast-chat-comment-color: #00A3ED;
    }

    :host(:last-of-type) {
      margin-bottom: 0;
    }

    #avatar {
      grid-area: 1 / 1;
      align-self: flex-end;
    }

    #content {
      grid-area: 1 / 2 / 1 / 5;
      border-radius: 14px;
      background: rgba(255,255,255,0.1);
      padding: 10px;
      justify-self: flex-start;
      min-width: 35%;
    }

    #content.current {
      background: var(--v-ui-color-secondary);
      --v-ui-color-link: black;
      --v-broadcast-chat-comment-color: white;
    }

    #content ::slotted(*) {
      margin: 0 0 8px;
      user-select: text;
      word-break: break-word;
    }

    #content ::slotted(*:last-child) {
      margin: 0;
    }

    #container.quote #content {
      grid-area: 1;
      background: none;
      border-radius: 0;
      padding: 0 0 0 6px;
      border-left: 2px solid var(--v-broadcast-chat-comment-color);
    }

    #name {
      color: var(--v-broadcast-chat-comment-color);
    }

    #time {
      grid-area: 2 / 2;
    }

    #actions {
      grid-area: 2 / 3;
      display: flex;
      justify-content: space-between;
    }

    #likes {
      grid-area: 2 / 4;
    }

    #likes v-ui-text {
      display: grid;
      grid-auto-flow: column;
      grid-gap: 4px;
      align-items: center;
    }
  `,

  content:
    ({ comment }) =>
    (target) => {
      target.innerHTML = markdown.render(comment.content);
    },
});
